import { Injectable, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutes } from '@shared/helpers';
import { Subscription } from 'rxjs';

export const appRoutes = AppRoutes.routes();
export type SeqRoutesEnumKeys = keyof typeof appRoutes;

export const SeqPageTitles: Record<SeqRoutesEnumKeys, string> = {
    DASHBOARD: 'sequencing',
    ERROR: 'sequencing.title.error',
    FORBIDDEN: 'sequencing.title.forbidden',
    INVENTORY_BASE_URL: 'sequencing.title.inventory.primers',
    INVENTORY_PRIMERS: 'sequencing.title.inventory.primers',
    INVENTORY_SAMPLES: 'sequencing.title.inventory.samples',
    NOT_FOUND: 'sequencing.title.not-found',
    ORDERS_ABSTRACT_BASE_URL: 'sequencing.title.order.details',
    ORDERS_ABSTRACT_DETAILS: 'sequencing.title.order.details',
    ORDERS_ABSTRACT_RESULTS: 'sequencing.title.order.details',
    ORDERS_CHECKMATE_RETEST_LIST: 'sequencing.title.retest.list',
    ORDERS_CHECKMATE_RETEST_SETUP: 'sequencing.title.retest.setup',
    ORDERS_DRAFT: 'sequencing.title.order.setup',
    ORDERS_LIST: 'sequencing.title.order.list',
    ORDERS_REACTIONS_LIST: 'sequencing.title.reaction.list',
    ORDERS_SETUP: 'sequencing.title.order.setup',
    GROUP_SETTINGS_GENERAL: 'sequencing.title.groups.settings',
    GROUP_SETTINGS_MEMBERS: 'sequencing.title.groups.settings',
    GROUP_SETTINGS_PRICING: 'sequencing.title.groups.settings',
    GROUP_SETTINGS_BASE_URL: 'sequencing.title.groups.settings',
    GROUPS_LISTING: 'groups.page.title',
};

@Injectable()
export class SeqPageTitlesService implements OnDestroy {
    private subscriptions = new Subscription();

    constructor(
        private titleService: Title,
        private translateService: TranslateService,
        private router: Router
    ) {
        this.subscriptions.add(
            this.router.events.subscribe(event => {
                if (event instanceof NavigationEnd) {
                    this.computePageTitle(event.urlAfterRedirects);
                }
            })
        );
    }

    private computeRouteEnumValues() {
        return Object.keys(AppRoutes.routes())
            .map(key => {
                return {
                    key: key,
                    value: AppRoutes.routes()[key as SeqRoutesEnumKeys],
                };
            })
            .filter(obj => {
                return typeof obj.value === 'string';
            }) as { key: string; value: string }[];
    }

    private computePageTitle(currentUrl: string) {
        const routeFound = this.computeRouteEnumValues().find(routeValue => {
            return !!routeValue.value && currentUrl.includes(routeValue.value);
        });

        const untranslatedTitle = routeFound
            ? SeqPageTitles[routeFound.key as unknown as SeqRoutesEnumKeys]
            : SeqPageTitles.DASHBOARD;
        const title = this.translateService.instant(untranslatedTitle);
        this.titleService.setTitle(title);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
